import { redactUrl } from "src/utils/redact-url";

/**
 * The page path and URL handed to analytics vendors, with the order session
 * scrubbed out.
 *
 * The guest session token lives in the query string of every checkout page by
 * design — order-view pages read it from there (src/utils/order-session-url.ts).
 * It is a 14-day credential: it reads the order's name, address, phone, email
 * and presigned photo URLs, and it can mutate the cart.
 *
 * It was being sent verbatim to Google Analytics as `page_path` and to the Meta
 * Conversions API as `page_url`. That is the same class of mistake as the
 * removed file.io upload — a customer credential handed to a third party — and
 * the scrubber to prevent it already existed for Sentry. It just was not
 * applied here.
 *
 * Marketing params (gclid, utm_*, fbclid) are deliberately left alone: they are
 * what the tracking is for.
 *
 * This covers only what WE hand over. The Meta Pixel, GA4 (`page_location`)
 * and Clarity read the document location themselves and never see these
 * values; what keeps the token away from them is that it is no longer in the
 * location at all — src/utils/guest-token-url-guard.ts. The scrub stays as the
 * second line, for a URL that still carries it.
 */
export function analyticsPath(pathname: string, search?: string): string {
  const path = pathname + (search ? `?${search}` : "");
  return redactUrl(path);
}

/** The same, absolute, for the Meta Conversions API. */
export function analyticsUrl(
  origin: string,
  pathname: string,
  search?: string,
): string {
  return origin + analyticsPath(pathname, search);
}
